import { useEffect } from 'react';
import {
  getAllOrganizations,
  getFacilitiesByOrganization,
  getDataUnitsByFacility,
} from '../services/apiServices';
import { FETCH_FACILITIES_AFTER_LOGIN } from '../services/config';
import { NO_FACILITIES_AVAILABLE, NO_UNITS_AVAILABLE, USER_ROLES } from '../constants';
import { serviceLogger } from '../utils/serviceLogger';

const setLoadingState = (setLoading, key, value) => {
  setLoading((prev) => ({ ...prev, [key]: value }));
};

export const useFetchOrganizations = ({ userData, userType, setLoading, setOrganizations }) => {
  useEffect(() => {
    if (!userData || userType !== USER_ROLES.ADMIN) return;

    const fetch = async () => {
      setLoadingState(setLoading, 'organizations', true);
      try {
        const data = await getAllOrganizations();
        setOrganizations(data);
      } catch (error) {
        serviceLogger.error('Error fetching organizations:', error);
      } finally {
        setLoadingState(setLoading, 'organizations', false);
      }
    };

    fetch();
  }, [userData, userType, setLoading, setOrganizations]);
};

export const useFetchFacilities = ({
  userData,
  userType,
  selectedOrganization,
  setSelectedOrganization,
  setFacilities,
  setFacilityError,
  setLoading,
}) => {
  useEffect(() => {
    if (!userData || (!selectedOrganization && userType === USER_ROLES.ADMIN)) return;

    const fetch = async () => {
      setLoadingState(setLoading, 'facilities', true);
      setFacilityError('');

      try {
        const organizationId =
          userType === USER_ROLES.ADMIN
            ? selectedOrganization
            : (userData?.organization ?? userData?.organizationId);
        if (userType !== USER_ROLES.ADMIN) setSelectedOrganization(organizationId);

        if (!organizationId || !FETCH_FACILITIES_AFTER_LOGIN) {
          setFacilities([]);
          setLoadingState(setLoading, 'facilities', false);
          return;
        }
        const data = await getFacilitiesByOrganization(organizationId);
        setFacilities(data);
        if (!data?.length) setFacilityError(NO_FACILITIES_AVAILABLE);
      } catch (error) {
        serviceLogger.error('Error fetching facilities:', error);
        const msg =
          error?.response?.status === 404
            ? NO_FACILITIES_AVAILABLE
            : 'Error fetching facilities. Please try again later.';
        setFacilityError(msg);
      } finally {
        setLoadingState(setLoading, 'facilities', false);
      }
    };

    fetch();
  }, [
    userData,
    userType,
    selectedOrganization,
    setSelectedOrganization,
    setFacilities,
    setFacilityError,
    setLoading,
  ]);
};

export const useFetchUnits = ({ selectedFacility, setUnits, setUnitError, setLoading }) => {
  useEffect(() => {
    if (!selectedFacility) {
      setUnits([]);
      setUnitError('');
      return;
    }

    const fetch = async () => {
      setLoadingState(setLoading, 'units', true);
      try {
        const data = await getDataUnitsByFacility(selectedFacility);
        setUnits(data);
        setUnitError(data?.length ? '' : NO_UNITS_AVAILABLE);
      } catch (error) {
        serviceLogger.error(`Error fetching units for facility ID ${selectedFacility}:`, error);
        setUnits([]);
        setUnitError(NO_UNITS_AVAILABLE);
      } finally {
        setLoadingState(setLoading, 'units', false);
      }
    };

    fetch();
  }, [selectedFacility, setUnits, setUnitError, setLoading]);
};
